import { useState, useRef, useEffect } from "react";
import { useProfileVideos } from "@/hooks/useProfileVideos";
import { ChevronLeft, ChevronRight, Play, Lock, Volume2, VolumeX } from "lucide-react";
import VideoUnlockConfetti from "./VideoUnlockConfetti";

interface ProfileVideoCarouselProps {
  userId: string;
  isMatched: boolean;
  userName?: string;
}

const UNLOCK_SHOWN_KEY = "video-unlock-shown-";

const ProfileVideoCarousel = ({ userId, isMatched, userName }: ProfileVideoCarouselProps) => {
  const { loading, getVisibleVideos, totalCount } = useProfileVideos(userId);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isMuted, setIsMuted] = useState(true);
  const [showConfetti, setShowConfetti] = useState(false);
  const videoRef = useRef<HTMLVideoElement>(null);

  const visibleVideos = getVisibleVideos(isMatched);
  const hiddenCount = totalCount - visibleVideos.length;
  const currentVideo = visibleVideos[currentIndex];

  // Check if we should show unlock celebration
  useEffect(() => {
    if (isMatched && totalCount > 0) {
      const unlockKey = `${UNLOCK_SHOWN_KEY}${userId}`;
      const alreadyShown = sessionStorage.getItem(unlockKey);
      
      // Show confetti if matched, have videos, and haven't shown before this session
      if (!alreadyShown && hiddenCount === 0 && visibleVideos.length > 0) {
        // Small delay to let the UI render first
        const timer = setTimeout(() => {
          setShowConfetti(true);
          sessionStorage.setItem(unlockKey, "true");
        }, 500);
        
        return () => clearTimeout(timer);
      }
    }
  }, [isMatched, userId, totalCount, hiddenCount, visibleVideos.length]);

  // Reset on user change
  useEffect(() => {
    setCurrentIndex(0);
    setIsPlaying(false);
    setShowConfetti(false);
  }, [userId]);

  const goToNext = () => {
    if (visibleVideos.length <= 1) return;
    setIsPlaying(false);
    setCurrentIndex((prev) => (prev + 1) % visibleVideos.length);
  };

  const goToPrev = () => {
    if (visibleVideos.length <= 1) return;
    setIsPlaying(false);
    setCurrentIndex((prev) => (prev - 1 + visibleVideos.length) % visibleVideos.length);
  };

  const togglePlay = () => {
    if (!videoRef.current) return;
    if (isPlaying) {
      videoRef.current.pause();
    } else {
      videoRef.current.play();
    }
    setIsPlaying(!isPlaying);
  };

  if (loading) {
    return (
      <div className="w-full aspect-[9/16] bg-muted rounded-xl animate-pulse" />
    );
  }

  if (visibleVideos.length === 0) {
    return (
      <div className="w-full aspect-[9/16] bg-gradient-to-b from-muted to-muted/50 rounded-xl flex flex-col items-center justify-center gap-3 text-center p-4">
        <div className="w-16 h-16 rounded-full bg-muted-foreground/10 flex items-center justify-center">
          <Play className="w-8 h-8 text-muted-foreground/50" />
        </div>
        <p className="text-muted-foreground text-sm">
          {hiddenCount > 0 && !isMatched ? (
            <>
              <Lock className="w-4 h-4 inline mr-1" />
              {hiddenCount} video{hiddenCount > 1 ? "s" : ""} unlock after matching
            </>
          ) : (
            `${userName || "This user"} hasn't added any videos yet`
          )}
        </p>
      </div>
    );
  }

  const videoUrl = currentVideo.video_url || 
    `${import.meta.env.VITE_SUPABASE_URL}/storage/v1/object/public/profile-videos/${currentVideo.storage_path}`;

  return (
    <div className="relative w-full aspect-[9/16] bg-black rounded-xl overflow-hidden">
      {/* Confetti celebration */}
      <VideoUnlockConfetti show={showConfetti} />

      {/* Video */}
      <video
        ref={videoRef}
        key={currentVideo.id}
        src={videoUrl}
        className="w-full h-full object-cover"
        playsInline
        muted={isMuted}
        onEnded={() => {
          setIsPlaying(false);
          // Auto-advance to next video
          if (visibleVideos.length > 1) {
            setTimeout(goToNext, 500);
          }
        }}
        onClick={togglePlay}
      />

      {/* Gradient overlay */}
      <div className="absolute inset-0 bg-gradient-to-b from-black/20 via-transparent to-black/40 pointer-events-none" />

      {/* Play/pause overlay */}
      {!isPlaying && (
        <button
          onClick={togglePlay}
          className="absolute inset-0 flex items-center justify-center"
        >
          <div className="w-16 h-16 rounded-full bg-white/30 backdrop-blur-sm flex items-center justify-center">
            <Play className="w-8 h-8 text-white fill-current ml-1" />
          </div>
        </button>
      )}

      {/* Navigation arrows (if multiple videos) */}
      {visibleVideos.length > 1 && (
        <>
          <button
            onClick={goToPrev}
            className="absolute left-2 top-1/2 -translate-y-1/2 w-8 h-8 rounded-full bg-black/40 flex items-center justify-center text-white hover:bg-black/60 transition-colors"
          >
            <ChevronLeft className="w-5 h-5" />
          </button>
          <button
            onClick={goToNext}
            className="absolute right-2 top-1/2 -translate-y-1/2 w-8 h-8 rounded-full bg-black/40 flex items-center justify-center text-white hover:bg-black/60 transition-colors"
          >
            <ChevronRight className="w-5 h-5" />
          </button>
        </>
      )}

      {/* Mute toggle */}
      <button
        onClick={() => setIsMuted(!isMuted)}
        className="absolute bottom-4 right-4 w-10 h-10 rounded-full bg-black/40 flex items-center justify-center text-white hover:bg-black/60 transition-colors"
      >
        {isMuted ? <VolumeX className="w-5 h-5" /> : <Volume2 className="w-5 h-5" />}
      </button>

      {/* Video indicators */}
      {visibleVideos.length > 1 && (
        <div className="absolute top-4 left-4 right-4 flex gap-1">
          {visibleVideos.map((_, idx) => (
            <button
              key={idx}
              onClick={() => {
                setIsPlaying(false);
                setCurrentIndex(idx);
              }}
              className={`h-1 flex-1 rounded-full transition-colors ${
                idx === currentIndex ? "bg-white" : "bg-white/40"
              }`}
            />
          ))}
        </div>
      )}

      {/* Hidden videos hint */}
      {hiddenCount > 0 && !isMatched && (
        <div className="absolute bottom-4 left-4 px-3 py-1.5 rounded-full bg-black/60 text-white text-xs flex items-center gap-1.5">
          <Lock className="w-3 h-3" />
          +{hiddenCount} more after match
        </div>
      )}

      {/* Video counter */}
      {visibleVideos.length > 1 && (
        <div className="absolute top-4 right-4 px-2 py-1 rounded-full bg-black/60 text-white text-xs">
          {currentIndex + 1}/{visibleVideos.length}
        </div>
      )}
    </div>
  );
};

export default ProfileVideoCarousel;
